// ===========================================
// "Spam check unavailable" chip (#412)
// ===========================================
//
// A public enquiry or Meet & Greet booking saved while the Cloudflare Turnstile
// check could not run (`spamCheck === 'UNVERIFIED'`) got NO automatic email: the
// enquirer no confirmation, the booking family no verification link. The office
// should reply by hand, or for a booking press "Send verification email" (the
// admin page's own label for a booking with nothing sent). Plan md/plan/412.md D23.

import { Chip, Tooltip, type SxProps, type Theme } from '@mui/material';

export type SpamCheckStatus = 'VERIFIED' | 'UNVERIFIED' | 'NOT_CONFIGURED';

interface SpamCheckChipProps {
  spamCheck: SpamCheckStatus | null | undefined;
  /** What the office should do, shown on hover. */
  tooltip: string;
  /**
   * Whether the warning is still actionable. A booking stays UNVERIFIED for life,
   * so the M&G page passes false once it is verified or an email has been sent
   * (/qa Phase 4 L6). Defaults to true.
   */
  actionable?: boolean;
  /** Layout only (e.g. a left margin in a header), merged after the chip's own style. */
  sx?: SxProps<Theme>;
}

export default function SpamCheckChip({ spamCheck, tooltip, actionable = true, sx }: SpamCheckChipProps) {
  if (spamCheck !== 'UNVERIFIED' || !actionable) return null;
  // FILLED with dark text: an outlined warning chip draws coral #FFAE9E text on
  // white (~1.8:1, fails WCAG AA); this is ~11:1 (/qa Phase 5 M2, #563).
  return (
    <Tooltip title={tooltip}>
      <Chip
        label="Spam check unavailable"
        size="small"
        data-testid="spam-check-chip"
        sx={[
          { bgcolor: 'warning.main', color: '#080808', fontWeight: 600, flexShrink: 0 },
          ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
        ]}
      />
    </Tooltip>
  );
}

export const ENQUIRY_SPAM_CHECK_TOOLTIP =
  'Received while the spam check was unavailable, so no automatic reply was sent. Reply by hand.';

export const MEET_AND_GREET_SPAM_CHECK_TOOLTIP =
  'No verification email was sent. Use Send verification email.';
